import React from "react";
import { Link } from "react-router-dom";
import { Lock, Boxes, FileCheck2, PackageSearch, ArrowRight, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { Seo } from "../components/Seo";
import { Reveal, Stagger, itemVariant } from "../components/Reveal";
import { COMPANY } from "../data/content";

export default function Portal() {
  const { t, lang } = useLang();
  const feats = [
    { icon: Lock, de: { t: "Kundenlogin", d: "Sicherer, kundenspezifischer Zugang." }, en: { t: "Customer login", d: "Secure, customer-specific access." } },
    { icon: PackageSearch, de: { t: "Verfügbarkeiten", d: "Lager- und Beschaffungsstatus auf einen Blick." }, en: { t: "Availability", d: "Stock and procurement status at a glance." } },
    { icon: FileCheck2, de: { t: "Datenblätter & Zertifikate", d: "TDS, SDS und COA jederzeit abrufbar." }, en: { t: "Data sheets & certificates", d: "TDS, SDS and COA available anytime." } },
    { icon: Boxes, de: { t: "Muster & Angebote", d: "Musteranfragen, Angebote und Bestellhistorie." }, en: { t: "Samples & quotes", d: "Sample requests, quotes and order history." } },
  ];
  return (
    <>
      <Seo title="Kundenportal | PlastoNord"
        description="Das PlastoNord Kundenportal: kundenspezifische Produktsichtbarkeit, Verfügbarkeiten, Datenblätter, Zertifikate, Muster- und Angebotsanfragen unter portal.plasto-nord.com."
        path="/kundenportal" />
      <section className="relative overflow-hidden bg-[#16181D] text-white grain">
        <div className="absolute inset-0 dot-grid opacity-20" />
        <div className="pn-container relative py-24 md:py-32">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium"><span className="h-2 w-2 rounded-full bg-[#E2611C]" /> {lang === "de" ? "Jetzt verfügbar" : "Now available"}</span>
            <h1 className="mt-6 max-w-3xl font-head text-4xl font-light tracking-tight md:text-6xl">{t("nav.portal")}</h1>
            <p className="mt-6 max-w-2xl text-lg text-white/70">{lang === "de" ? "Unser sicheres Kundenportal mit kundenspezifischer Produktsichtbarkeit, Verfügbarkeiten und Dokumenten – erreichbar unter portal.plasto-nord.com." : "Our secure customer portal with customer-specific product visibility, availability and documents – available at portal.plasto-nord.com."}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <a href={COMPANY.portalUrl} className="btn-primary !bg-[#E2611C]" data-testid="portal-external">portal.plasto-nord.com <ArrowUpRight size={16} /></a>
              <Link to="/kontakt" className="btn-ghost !border-white/40 !text-white hover:!bg-white hover:!text-[#16181D]">{t("cta.contact")} <ArrowRight size={16} /></Link>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="py-24">
        <div className="pn-container">
          <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {feats.map((f) => { const c = lang === "de" ? f.de : f.en; return (
              <motion.div key={c.t} variants={itemVariant} className="pn-card p-7">
                <span className="grid h-11 w-11 place-items-center rounded-lg bg-[#FBEADD] text-[#16181D]"><f.icon size={19} /></span>
                <h3 className="mt-4 font-head text-base font-semibold text-[#16181D]">{c.t}</h3>
                <p className="mt-2 text-sm text-[#4a4d52]">{c.d}</p>
              </motion.div>
            ); })}
          </Stagger>
          <p className="mt-10 text-center text-sm text-[#4a4d52]">{lang === "de" ? "Noch keinen Zugang? Kontaktieren Sie uns – wir schalten Ihren kundenspezifischen Portalzugang frei." : "No access yet? Contact us – we will activate your customer-specific portal access."}</p>
        </div>
      </section>
    </>
  );
}
